// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { InstrumentConfiguration } from '@digitalasset/utility-operator-api-ts-client';
import useRegistryInstrumentConfigurationFactory from '@hooks/queries/registry/configuration/useRegistryInstrumentConfigurationFactory';
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Alert, Box } from '@mui/material';
import { InstrumentConfiguration as DamlInstrumentConfiguration } from '@daml.js/dtc-registry/lib/Dtc/Registry/V3/Configuration/Instrument';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import CustomPropertiesInput from '../../../components/Form/CustomPropertiesInput';
import TextInput from '../../../components/Form/TextInput';
import {
  PropertyEntry,
  getPropertyErrors,
  propertyEntriesFromMap,
  propertyEntriesToMap,
} from '../../../components/Form/customPropertiesValidation';
import {
  getValidateAlphanumericMessage,
  getValidateVisibleMessage,
} from '../../../components/Form/fieldValidation';
import useArchiveAndCreateDtccTokenInstrumentConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveAndCreateDtccTokenInstrumentConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import { useGetAllInstrumentConfigurations } from '../../../hooks/queries/useGetAllInstrumentConfigurations';
import { getInstrumentOverlapWarning } from '../../../utils/instrumentOverlap';
import { SUC_MOD_INST_CONF, ERR_MOD_INST_CONF } from '../../../utils/strings';
import InstrumentIssueTypeSelect from './InstrumentIssueTypeSelect';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  instrumentConfiguration: InstrumentConfiguration;
};

export const ModifyDtccTokenInstrumentConfigurationDialog = ({
  open,
  onClose,
  instrumentConfiguration,
}: DialogProps) => {
  const payload = instrumentConfiguration.payload;

  const [tokenName, setTokenName] = useState<string>(payload.tokenName);
  const [tokenSymbol, setTokenSymbol] = useState<string>(payload.tokenSymbol);
  const [cusip, setCusip] = useState<string>(payload.cusip);
  const [shortDescription, setShortDescription] = useState<string>(payload.shortDescription);
  const [properties, setProperties] = useState<PropertyEntry[]>(
    propertyEntriesFromMap(payload.meta),
  );

  const instType = payload.issueType;
  const instSubtype = payload.issueSubtype;

  const [instrumentIssueType, setInstrumentIssueType] = useState<string>(
    instType && instSubtype ? `${instType}-${instSubtype}`.toLowerCase() : '',
  );

  const mutate = useMutate();
  const { showBoundary } = useErrorBoundary<Error>();
  const registrarService = useRegistrarService();
  const instrumentConfigurationFactory = useRegistryInstrumentConfigurationFactory();
  const instrumentConfigurations = useGetAllInstrumentConfigurations();
  const archiveAndCreateDtccTokenInstrumentConfiguration =
    useArchiveAndCreateDtccTokenInstrumentConfiguration();

  const resetState = useCallback(() => {
    setTokenName(payload.tokenName);
    setTokenSymbol(payload.tokenSymbol);
    setCusip(payload.cusip);
    setShortDescription(payload.shortDescription);
    setInstrumentIssueType(
      payload.issueType && payload.issueSubtype
        ? `${payload.issueType}-${payload.issueSubtype}`.toLowerCase()
        : '',
    );
    setProperties(propertyEntriesFromMap(payload.meta));
  }, [
    payload,
    setTokenName,
    setTokenSymbol,
    setCusip,
    setShortDescription,
    setInstrumentIssueType,
    setProperties,
  ]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [resetState, onClose]);

  const handleSubmit = useCallback(async () => {
    if (!registrarService.data) {
      showBoundary(new Error(ERR_MOD_INST_CONF, { cause: 'Registrar service is not available.' }));
      return;
    }
    if (!instrumentConfigurationFactory.data) {
      showBoundary(
        new Error(ERR_MOD_INST_CONF, { cause: 'InstrumentConfigurationFactory is not available.' }),
      );
      return;
    }
    const [issueType, issueSubtype] = instrumentIssueType.split('-');
    const payload = {
      registrarServiceCid: registrarService.data.contractId,
      instrumentConfigurationFactoryCid: instrumentConfigurationFactory.data.contractId,
      instrumentConfigurationCid: instrumentConfiguration.contract
        .contractId as ContractId<DamlInstrumentConfiguration>,
      tokenName,
      tokenSymbol,
      cusip,
      shortDescription,
      issueType,
      issueSubtype,
      properties: propertyEntriesToMap(properties),
    };
    await mutate(
      archiveAndCreateDtccTokenInstrumentConfiguration,
      payload,
      SUC_MOD_INST_CONF,
      ERR_MOD_INST_CONF,
    );
    handleClose();
  }, [
    registrarService.data,
    instrumentConfigurationFactory.data,
    instrumentConfiguration.contract.contractId,
    tokenName,
    tokenSymbol,
    cusip,
    shortDescription,
    instrumentIssueType,
    properties,
    handleClose,
    mutate,
    showBoundary,
    archiveAndCreateDtccTokenInstrumentConfiguration,
  ]);

  if (registrarService.isLoading || archiveAndCreateDtccTokenInstrumentConfiguration.isLoading)
    return null;

  const propertyErrors = getPropertyErrors(properties);
  const hasPropertyErrors = propertyErrors.some((e) => e !== '');
  const tokenNameValidationMessage = getValidateVisibleMessage(tokenName);
  const cusipValidationMessage = getValidateAlphanumericMessage(cusip);
  const tokenSymbolValidationMessage = getValidateAlphanumericMessage(tokenSymbol);
  const overlapWarning = getInstrumentOverlapWarning(
    instrumentConfigurations.data?.instrumentConfigurations,
    {
      tokenName,
      tokenSymbol,
      cusip,
      excludeId: payload.defaultIdentifier.id,
    },
  );
  const disableSubmit =
    !tokenName ||
    !tokenSymbol ||
    !cusip ||
    !instrumentIssueType ||
    hasPropertyErrors ||
    !!tokenNameValidationMessage ||
    !!cusipValidationMessage ||
    !!tokenSymbolValidationMessage;

  return (
    <FormDialog
      open={open}
      title="Modify DTCC Token"
      onCancel={handleClose}
      onSubmit={handleSubmit}
      submitName="Modify"
      isSubmitDisabled={disableSubmit}
    >
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="Token Name"
          value={tokenName}
          setValue={(value) => {
            setTokenName(value);
          }}
          required
          validationMessage={tokenNameValidationMessage}
        />
      </Box>
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="Token Symbol"
          value={tokenSymbol}
          setValue={(value) => {
            setTokenSymbol(value);
          }}
          required
          validationMessage={tokenSymbolValidationMessage}
        />
      </Box>
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="CUSIP"
          value={cusip}
          setValue={(value) => {
            setCusip(value);
          }}
          required
          validationMessage={cusipValidationMessage}
        />
      </Box>
      <Box mb={3}>
        <InstrumentIssueTypeSelect value={instrumentIssueType} setValue={setInstrumentIssueType} />
      </Box>
      <Box mb={3}>
        <TextInput
          enableTopLabel
          label="Short Description"
          value={shortDescription}
          setValue={(value) => {
            setShortDescription(value);
          }}
        />
      </Box>
      <Box mb={3}>
        <CustomPropertiesInput
          properties={properties}
          setProperties={setProperties}
          errors={propertyErrors}
        />
      </Box>
      {overlapWarning && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {overlapWarning}
        </Alert>
      )}
    </FormDialog>
  );
};
